import {Injectable, Injector} from "@angular/core";
import {Observable, throwError, catchError, finalize, map, tap} from "rxjs";
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {PolicyAuthJson} from "../model/policy.auth-model";
import {DateService} from "../ui/ui-datepicker/date.service";
import { SpinnerService } from '../core/spinner/spinner.service';
import { ErrorsService } from "../utilities/errors/errors.service";
import { PolicyVerifyForm } from "../model/policy-verify-form.model";


@Injectable()
export class IntegrationService {

    constructor(private http: HttpClient,
                private dateService: DateService,
                private spinnerService :  SpinnerService,
                private injector: Injector) {
    }
    public pendingRequests: number = 0;

    // Check whether the policy exists.
    public checkPolicyNumber(policyVerifyForm: PolicyVerifyForm): Observable<PolicyAuthJson> {

        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type':  'application/json'
            })
        };

        const url: string = '/ms/claims-service/rest/check/policy';
        let formString : string = JSON.stringify(policyVerifyForm);

        this.spinnerService.displayLoader(true);

        return this.http.post<PolicyAuthJson>(url, formString, httpOptions)
            .pipe(
                tap(() => {
                    this.spinnerService.displayLoader(false);
                }),
                map((res: PolicyAuthJson) => {
                    this.pendingRequests++;
                    return this.mapPolicyAuth(res);
                }),
                catchError<any, Observable<PolicyAuthJson>>(error => {
                    this.spinnerService.displayLoader(false);
                    return this.handleError(error);
                })
            );

    }

    // Check the security info against the policy to verify if we can continue
    public verifyPolicyNumber(policyVerifyForm: PolicyVerifyForm): Observable<PolicyAuthJson> {

        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type':  'application/json'
            })
        };

        this.spinnerService.displayLoader(true);

        const url: string = '/ms/claims-service/rest/verify/policy';
        let formString : string = JSON.stringify(policyVerifyForm);

        return this.http.post<PolicyAuthJson>(url, formString, httpOptions)
            .pipe(map((res : PolicyAuthJson) => {

                console.log('http return result');
                console.log(res);
                let policyAuthJson = this.mapPolicyAuth(res);
                this.spinnerService.displayLoader(false);
                return policyAuthJson;
            }))
            //...errors if any
            .pipe(catchError(this.handleError))
            .pipe(finalize( () => {this.spinnerService.displayLoader(false) }));
    }

    // bypass security info against for cover note and pre numbered policy - motor
    public verifyCoverNote(policyVerifyForm: PolicyVerifyForm): Observable<PolicyAuthJson> {

        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type':  'application/json'
            })
        };

        const url: string = '/ms/claims-service/rest/verify/coverNote';
        let formString : string = JSON.stringify(policyVerifyForm);

        this.spinnerService.displayLoader(true);

        return this.http.post<PolicyAuthJson>(url, formString, httpOptions)
        // ...and calling .json() on the response to return data
            .pipe(map((res: PolicyAuthJson) => {

                console.log('http return result');
                console.log(res);
                let policyAuthJson = this.mapPolicyAuth(res);
                this.spinnerService.displayLoader(false);
                return policyAuthJson;
            }))
            //...errors if any
            .pipe(catchError(this.handleError))
            .pipe(finalize( () => {this.spinnerService.displayLoader(false) }));
    }


    private mapPolicyAuth(res : PolicyAuthJson) : PolicyAuthJson {
        let policyAuthJson: PolicyAuthJson = Object.assign(new PolicyAuthJson(), res);
        policyAuthJson  = PolicyAuthJson.jsonConvert(policyAuthJson);
        return policyAuthJson;
    }

    public getIntegrationData(integrationToken: string, policyType: string): Observable<PolicyAuthJson> {
        // validate policyType (allow only alphanumeric characters)
        if (!/^[a-zA-Z0-9]+$/.test(policyType)) {
            return throwError(() => new Error('Invalid policyType value'));
        }

        // validate integrationToken (ensure it's not empty or malformed)
        if (!integrationToken || typeof integrationToken !== 'string' || integrationToken.trim() === '') {
            return throwError(() => new Error('Invalid integrationToken value'));
        }

        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${integrationToken.trim()}`
            })
        };

        this.spinnerService.displayLoader(true);

        // use HttpParams to safely construct query parameters
        const params = new HttpParams().set('policyType', policyType);
        const url: string = '/ms/claims-service/rest/verify/ebusiness/policy';

        return this.http.get<PolicyAuthJson>(url, { ...httpOptions, params })
            .pipe(
                map((res: PolicyAuthJson) => {
                    return this.mapPolicyAuth(res);
                }),
                catchError<any, Observable<PolicyAuthJson>>(this.handleError),
                finalize(() => {
                    this.spinnerService.displayLoader(false);
                })
            );
    }

    private handleError(error: Response | any) {
        // In a real world app, you might use a remote logging infrastructure
        const errorsService = this.injector.get(ErrorsService);
        let errMsg: string;
        if (error instanceof Response) {
            errMsg = `${error.status} - ${error.statusText || ''}`;
        } else {
            errMsg = error.message ? error.message : error.toString();
        }
        errMsg = 'IntegrationService handleError' + errMsg;
        errorsService.log(errMsg);
        return throwError(() => new Error(errMsg));
    }

    getSystemStatus(country: string): Observable<string> {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };
        return this.http.get<string>('/ms/claims-service/rest/system/getStatus/'+ country, httpOptions);
    }

    public verifyRedirectToken(token: string, iv: string): Observable<any> {
        let url: string = '/ms/claims-service/rest/verify/redirectUrlToken?token=' + token + '&iv=' + iv;
        return this.http.get(url);
    }

     getPaymentMethodStatus(integrationToken: string, method: string): Observable<boolean> {
        this.spinnerService.displayLoader(true);

        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + integrationToken
            })
        };

        return this.http.get<string>(`/ms/claims-service/rest/get/paymentMethod/status/${method}`, httpOptions)
            .pipe(
                finalize(() => {
                    this.spinnerService.displayLoader(false);
                }),
                map(status => status?.toString() === 'true')
            );
    }

}
